import {Component, OnInit, inject} from '@angular/core';
import {SupportContactService} from '../../../_services/support-contact.service';

/**
 * The read-only banner shown to a non-owner on both legal pages (#613) — provider selection, credentials and
 * every TSE state change moved behind `[DynamicAuthorize("Legal", MetaRole.Owner)]`, so a `ShardAdmin` reaches
 * the page (unchanged `Permissions_Legal*Component` route gate) but every mutating action there now 403s.
 * Rather than let that surface as an opaque failed request, both pages render this hint instead of their
 * owner-only controls and tell the reader who to contact.
 *
 * Standalone and shared (not owned by either page) because both the TSE page and the settings page need the
 * identical sentence — a per-page copy is a copy that drifts, exactly the reasoning
 * `_auth/two-factor/second-factor-errors.ts` gives for its own "one mapping, several surfaces" rule.
 *
 * ## The wording (#241)
 *
 * It used to read „Änderungen an der technischen Sicherheitseinrichtung und an den **Fiskal-Providern** nimmt
 * der CheqMe-Support für Sie vor." Two things were wrong with that and both are fixed here:
 *
 * 1. **It named a provider.** Feature #241's standing rule is that no supplier is named anywhere a customer or
 *    their adviser can see; „Fiskal-Provider" is the vocabulary of the supplier relationship, which is
 *    CheqMe's and not the reader's. The sentence now names what the reader is looking at — the technische
 *    Sicherheitseinrichtung and the settings that belong to it — which is both true and provider-free, and is
 *    pinned by `legal-supplier-identity.spec.ts`.
 * 2. **It read as a sentence about an administrator.** Since #769 the TSE page is also read by the company's
 *    `Steuerberater`, who is not an administrator of anything and for whom "changes are made for you" is
 *    beside the point. Leading with „können Sie hier nur einsehen" states the reader's own situation first —
 *    true for both — and the support address follows as the way out for whoever does want a change.
 *
 * Formal `Sie`/`Ihr` throughout, per the project's German copy rule. Deliberately one sentence: this is a note
 * above the content, not an explanation of the authorization model.
 */
@Component({
  selector: 'app-legal-readonly-hint',
  standalone: true,
  template: `
    <div class="rounded-2xl border border-theme-primary-100/50 bg-theme-primary-50/50 p-4 text-sm text-gray-700"
         role="note">
      <p>
        Die technische Sicherheitseinrichtung und die zugehörigen Einstellungen können Sie hier nur einsehen;
        Änderungen nimmt der CheqMe-Support für Sie vor.
        @if (email(); as address) {
          Kontakt: <a class="font-semibold text-theme-primary-700 hover:text-theme-primary-800"
                       [href]="'mailto:' + address">{{ address }}</a>
        }
      </p>
    </div>
  `
})
export class LegalReadonlyHintComponent implements OnInit {
  private readonly supportContactService = inject(SupportContactService);

  protected readonly email = this.supportContactService.email;

  ngOnInit(): void {
    this.supportContactService.ensureLoaded();
  }
}
